import { apiRequest } from "../../../shared/lib/api";
import type {
  AdminEventChallengeLink,
  AdminEventRegistration,
  AdminTournamentBracket,
  AdminTournamentCategoryMatchSettings,
  AdminTournamentRegistrationSummaryResponse,
  AdminTournamentRegistrationsResponse,
  AdminTournamentSchedule,
  AdminTournamentScheduleConfig,
  AdminTournamentMatchSetScore,
  EventChallengeLinkSummary,
  ParticipantCheckin,
} from "./eventRegistrationAdminTypes";

export type {
  AdminEventChallengeLink,
  AdminEventRegistration,
  AdminTournamentBracket,
  AdminTournamentBracketMatch,
  AdminTournamentBracketRound,
  AdminTournamentCategoryMatchSettings,
  AdminTournamentMatchSetScore,
  AdminTournamentRegistrationCategorySummary,
  AdminTournamentRegistrationSummaryResponse,
  AdminTournamentRegistrationsResponse,
  AdminTournamentSchedule,
  AdminTournamentScheduleConfig,
  AdminTournamentScheduleSlot,
  EventChallengeLinkSummary,
  ParticipantCheckin,
} from "./eventRegistrationAdminTypes";

export function listEventChallengeLinks(token: string) {
  return apiRequest<AdminEventChallengeLink[]>("/api/admin/event-challenge-links", {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export function upsertEventChallengeLink(token: string, eventId: string, tournamentId: number) {
  return apiRequest<{ ok: boolean }>(
    `/api/admin/event-challenge-links/${encodeURIComponent(eventId)}`,
    {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ tournamentId }),
    }
  );
}

export function deleteEventChallengeLink(token: string, eventId: string) {
  return apiRequest<{ ok: boolean }>(
    `/api/admin/event-challenge-links/${encodeURIComponent(eventId)}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

type ListRegistrationsFilters = {
  status?: "pending" | "confirmed" | "canceled" | "waitlist" | "all";
  eventId?: string;
  tournamentId?: number;
  registrationCategoryKey?: string;
  categoryKey?: string;
};

export function listEventRegistrations(token: string, filters?: ListRegistrationsFilters) {
  const params = new URLSearchParams();
  if (filters?.status && filters.status !== "all") params.set("status", filters.status);
  if (filters?.eventId) params.set("eventId", filters.eventId);
  if (filters?.tournamentId) params.set("tournamentId", String(filters.tournamentId));
  if (filters?.registrationCategoryKey) {
    params.set("registrationCategoryKey", filters.registrationCategoryKey);
  }
  if (filters?.categoryKey) params.set("categoryKey", filters.categoryKey);
  const query = params.toString();
  return apiRequest<AdminEventRegistration[]>(
    `/api/admin/event-registrations${query ? `?${query}` : ""}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

export function listTournamentRegistrationCategories(token: string, tournamentId: number) {
  return apiRequest<AdminTournamentRegistrationSummaryResponse>(
    `/api/admin/tournaments/${encodeURIComponent(String(tournamentId))}/registration-categories`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

export function listTournamentRegistrations(
  token: string,
  tournamentId: number,
  filters?: {
    status?: "pending" | "confirmed" | "canceled" | "waitlist" | "all";
    registrationCategoryKey?: string;
    categoryKey?: string;
  }
) {
  const params = new URLSearchParams();
  if (filters?.status && filters.status !== "all") params.set("status", filters.status);
  if (filters?.registrationCategoryKey) {
    params.set("registrationCategoryKey", filters.registrationCategoryKey);
  }
  if (filters?.categoryKey) params.set("categoryKey", filters.categoryKey);
  const query = params.toString();
  return apiRequest<AdminTournamentRegistrationsResponse>(
    `/api/admin/tournaments/${encodeURIComponent(String(tournamentId))}/registrations${
      query ? `?${query}` : ""
    }`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

export function getTournamentBracket(token: string, tournamentId: number, categoryKey: string) {
  return apiRequest<AdminTournamentBracket>(
    `/api/admin/tournaments/${encodeURIComponent(String(tournamentId))}/brackets/${encodeURIComponent(categoryKey)}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

export function generateTournamentBracket(
  token: string,
  tournamentId: number,
  categoryKey: string,
  payload?: {
    seedMode?: "ranking" | "random" | "manual";
    manualSeeds?: number[];
    manualGroups?: Array<{
      id?: string;
      name?: string;
      registrationIds: number[];
    }>;
  }
) {
  return apiRequest<AdminTournamentBracket>(
    `/api/admin/tournaments/${encodeURIComponent(String(tournamentId))}/brackets/${encodeURIComponent(categoryKey)}/generate`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload || {}),
    }
  );
}

export function publishTournamentBracket(
  token: string,
  tournamentId: number,
  categoryKey: string
) {
  return apiRequest<AdminTournamentBracket>(
    `/api/admin/tournaments/${encodeURIComponent(String(tournamentId))}/brackets/${encodeURIComponent(categoryKey)}/publish`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

export function publishTournamentBrackets(token: string, tournamentId: number) {
  return apiRequest<{
    tournamentId: number;
    publishedCount: number;
    categoryKeys: string[];
    brackets: AdminTournamentBracket[];
  }>(
    `/api/admin/tournaments/${encodeURIComponent(String(tournamentId))}/brackets/publish`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

export function getTournamentCategoryMatchSettings(
  token: string,
  tournamentId: number,
  categoryKey: string
) {
  return apiRequest<AdminTournamentCategoryMatchSettings>(
    `/api/admin/tournaments/${encodeURIComponent(String(tournamentId))}/brackets/${encodeURIComponent(categoryKey)}/settings`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

export function updateTournamentCategoryMatchSettings(
  token: string,
  tournamentId: number,
  categoryKey: string,
  payload: Pick<
    AdminTournamentCategoryMatchSettings,
    | "matchFormat"
    | "bestOfSets"
    | "gamesPerSet"
    | "tieBreakEnabled"
    | "tieBreakAt"
    | "tieBreakPoints"
    | "matchTieBreakPoints"
    | "finalMatchFormat"
    | "finalBestOfSets"
    | "finalGamesPerSet"
    | "finalTieBreakEnabled"
    | "finalTieBreakAt"
    | "finalTieBreakPoints"
    | "finalMatchTieBreakPoints"
  >
) {
  return apiRequest<AdminTournamentCategoryMatchSettings>(
    `/api/admin/tournaments/${encodeURIComponent(String(tournamentId))}/brackets/${encodeURIComponent(categoryKey)}/settings`,
    {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    }
  );
}

export function updateTournamentMatchScore(
  token: string,
  tournamentId: number,
  categoryKey: string,
  matchId: number,
  payload: { sets: AdminTournamentMatchSetScore[] }
) {
  return apiRequest<AdminTournamentBracket>(
    `/api/admin/tournaments/${encodeURIComponent(String(tournamentId))}/brackets/${encodeURIComponent(categoryKey)}/matches/${encodeURIComponent(String(matchId))}/score`,
    {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    }
  );
}

export function updateTournamentMatchParticipants(
  token: string,
  tournamentId: number,
  categoryKey: string,
  matchId: number,
  payload: { sideARegistrationId: number; sideBRegistrationId: number }
) {
  return apiRequest<AdminTournamentBracket>(
    `/api/admin/tournaments/${encodeURIComponent(String(tournamentId))}/brackets/${encodeURIComponent(categoryKey)}/matches/${encodeURIComponent(String(matchId))}/participants`,
    {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    }
  );
}

export function updateTournamentMatchStatus(
  token: string,
  tournamentId: number,
  categoryKey: string,
  matchId: number,
  payload: { status: "pending" | "scheduled" | "in_progress" }
) {
  return apiRequest<AdminTournamentBracket>(
    `/api/admin/tournaments/${encodeURIComponent(String(tournamentId))}/brackets/${encodeURIComponent(categoryKey)}/matches/${encodeURIComponent(String(matchId))}/status`,
    {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    }
  );
}

export function updateTournamentParticipantCheckin(
  token: string,
  tournamentId: number,
  cpf: string,
  checkedIn: boolean,
  matchId?: number | null
) {
  return apiRequest<{ ok: boolean; participantCheckins: ParticipantCheckin[] }>(
    `/api/admin/tournaments/${encodeURIComponent(String(tournamentId))}/checkins/${encodeURIComponent(cpf)}`,
    {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ checkedIn, matchId: matchId || null }),
    }
  );
}

export function updateChallengeMatchStatus(
  token: string,
  tournamentId: number,
  matchId: number,
  status: "pending" | "scheduled" | "in_progress"
) {
  return apiRequest<{
    ok: boolean;
    matchId: number;
    status: string;
    rounds: unknown[][];
  }>(
    `/api/admin/tournaments/${encodeURIComponent(String(tournamentId))}/challenge-matches/${encodeURIComponent(String(matchId))}/status`,
    {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status }),
    }
  );
}

export function getTournamentSchedule(token: string, tournamentId: number) {
  return apiRequest<AdminTournamentSchedule>(
    `/api/admin/tournaments/${encodeURIComponent(String(tournamentId))}/schedule`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
}

export function saveTournamentScheduleConfig(
  token: string,
  tournamentId: number,
  payload: Omit<AdminTournamentScheduleConfig, "tournamentId" | "updatedAt">
) {
  return apiRequest<{ tournamentId: number; config: AdminTournamentScheduleConfig }>(
    `/api/admin/tournaments/${encodeURIComponent(String(tournamentId))}/schedule/config`,
    {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    }
  );
}

export function generateTournamentSchedule(
  token: string,
  tournamentId: number,
  payload?: {
    config?: Omit<AdminTournamentScheduleConfig, "tournamentId" | "updatedAt">;
    regenerateAuto?: boolean;
  }
) {
  return apiRequest<AdminTournamentSchedule>(
    `/api/admin/tournaments/${encodeURIComponent(String(tournamentId))}/schedule/generate`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload || {}),
    }
  );
}

export function updateTournamentMatchSchedule(
  token: string,
  tournamentId: number,
  matchId: number,
  payload: {
    slotStartAt: string;
    slotEndAt?: string;
    courtName: string;
    durationMinutes?: number;
    allowRoundOrderOverride?: boolean;
    allowSwap?: boolean;
    allowCourtConflict?: boolean;
    lockReason?: string;
  }
) {
  return apiRequest<AdminTournamentSchedule>(
    `/api/admin/tournaments/${encodeURIComponent(String(tournamentId))}/schedule/matches/${encodeURIComponent(String(matchId))}`,
    {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    }
  );
}

export function approveEventRegistration(
  token: string,
  id: number,
  paymentRequired = false,
  adminNote = ""
) {
  return apiRequest<AdminEventRegistration>(`/api/admin/event-registrations/${id}`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ status: "confirmed", paymentRequired, adminNote }),
  });
}

export function rejectEventRegistration(token: string, id: number, adminNote = "") {
  return apiRequest<AdminEventRegistration>(`/api/admin/event-registrations/${id}`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ status: "canceled", adminNote }),
  });
}

export function markEventRegistrationPaid(
  token: string,
  id: number,
  paymentReference = "",
  paymentReferenceSecondary = "",
) {
  return apiRequest<AdminEventRegistration>(`/api/admin/event-registrations/${id}`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      action: "confirm_payment",
      paymentStatus: "confirmed",
      paymentReference,
      paymentReferenceSecondary,
    }),
  });
}

export function updateEventRegistrationPair(
  token: string,
  id: number,
  payload: {
    cpf: string;
    teammateCpf: string;
    registrationCategoryKey: string;
    shirtSizes?: {
      player?: string | null;
      partner?: string | null;
    };
    allowEligibilityOverride?: boolean;
    overrideReason?: string;
  }
) {
  return apiRequest<AdminEventRegistration>(`/api/admin/event-registrations/${id}`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      action: "update_pair",
      participantMode: "dupla_fixa",
      ...payload,
    }),
  });
}

export function updateEventRegistrationPreferredSide(
  token: string,
  id: number,
  preferredSide: "direita" | "esquerda" | "ambos",
) {
  return apiRequest<AdminEventRegistration>(`/api/admin/event-registrations/${id}`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      action: "update_preferred_side",
      preferredSide,
    }),
  });
}

export type AdminAvailabilityRestrictionPayload = {
  mode?: "impact" | "upsert" | "remove";
  restrictionId?: number;
  type?: "full_day" | "day_period" | "time_range";
  label?: string;
  startAt?: string;
  endAt?: string;
  amountCents?: number;
  paymentStatus?: "not_required" | "pending" | "confirmed";
  reason?: string;
  adminNotes?: string;
};

export function updateEventRegistrationAvailabilityRestriction(
  token: string,
  id: number,
  payload: AdminAvailabilityRestrictionPayload,
) {
  return apiRequest<{
    ok: boolean;
    impact?: {
      impactedCount: number;
      lockedCount: number;
      canAutoRecalculate?: boolean;
      matches?: Array<{
        matchId: number;
        label: string;
        slotStartAt: string;
        slotEndAt: string;
        courtName?: string | null;
        locked?: boolean;
      }>;
      lockedMatches?: Array<{
        matchId: number;
        label: string;
        slotStartAt: string;
        slotEndAt: string;
        courtName?: string | null;
        locked?: boolean;
      }>;
    };
    registration?: AdminEventRegistration;
  }>(`/api/admin/event-registrations/${id}/availability-restriction`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
}

export function generateAdminPaymentLink(
  token: string,
  id: number,
  payload?: { paymentMethod?: "pix" | "credit_card" | "debit_card" },
) {
  return apiRequest<{
    ok: boolean;
    paymentUrl?: string | null;
    invoiceUrl?: string | null;
    paymentReference?: string;
    selectedPaymentMethod?: "pix" | "credit_card" | "debit_card";
    amountCents?: number;
    baseAmountCents?: number;
    surchargeCents?: number;
    alreadyPaid?: boolean;
    external?: boolean;
    free?: boolean;
  }>(`/api/admin/event-registrations/${id}/payment-intent`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload || {}),
  });
}

export function deleteEventRegistration(token: string, id: number) {
  return apiRequest<{ ok: boolean; id: number }>(`/api/admin/event-registrations/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
}

export function createManualEventRegistration(
  token: string,
  eventId: string,
  payload: {
    userId?: number;
    cpf?: string;
    participantMode?: "individual" | "dupla_fixa";
    teammateCpf?: string;
    teammateName?: string;
    confirmPayment?: boolean;
    paymentMethod?: string;
    paymentReference?: string;
    adminNote?: string;
    registrationCategoryKey?: string;
    availabilityRestriction?: {
      enabled: boolean;
      periodKeys?: string[];
      periods?: Array<{
        periodKey?: string;
        label?: string;
        startAt?: string;
        endAt?: string;
        type?: string;
      }>;
    };
    preferredSide?: "direita" | "esquerda" | "ambos";
    shirtSizes?: {
      player?: string | null;
      partner?: string | null;
    };
    allowEligibilityOverride?: boolean;
    overrideReason?: string;
  }
) {
  return apiRequest<AdminEventRegistration>(
    `/api/admin/events/${encodeURIComponent(eventId)}/registrations/manual`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    }
  );
}

export function getEventChallengeLinkSummary(eventId: string) {
  return apiRequest<EventChallengeLinkSummary>(
    `/api/events/${encodeURIComponent(eventId)}/challenge-link`
  );
}
